/**
 * PackCreateModal — «Новая пачка», the create dialog of the blogger card's Инфо tab
 * (PACK-CREATE-UI 2026-08-31, spec §3.2).
 *
 * WHY IT EXISTS. Until this phase the only reachable pack-create flows were money-flow
 * surfaces (an invoice or a contract), and the standalone pack editor is unreachable dead
 * code — so a manager who simply wanted a pack had to invent an invoice. Owner ruling 1
 * (2026-08-31) puts pack creation — and the creation of the ambassador accounts that go
 * into it — back on the Инфо tab. This supersedes verdict Q6 («Амбассадорские пачки» is
 * read-only on Инфо) for CREATION only: pack *editing* stays in «Настройки пачки» on Статистика
 * (standing models §2, amended by the same verdict).
 *
 * SHELL. An ABSOLUTE scrim inside
 * `.details-modal` (styles.css:1321), so it covers the drawer and only the drawer;
 * Escape · scrim click · ✕ dismiss. Zero new CSS — every class below already exists
 * (lead-card.css:2113-2314), which is what gate `PCU-P2-NOCSS` holds.
 *
 * ONE CALL. Everything the dialog collects — label, type, the checked existing accounts
 * and the queued new ones — goes out in a single `POST /api/packs` (`useCreatePack`).
 * The route binds and upserts in one transaction, so there is no half-created state; a
 * POST-then-PATCH pair for the type would leave a `test` pack behind when the second call
 * failed. No cache code lives here: the hook already invalidates `packs.byLead`,
 * `accounts.detail(leadId)` and `leads.all`, which is what re-renders blocks 2 and 8.
 *
 * DUPLICATED, NOT ABSTRACTED (R5). The candidate filter is the gear's
 * (`PackSettingsModal.jsx:298-304`) with the one change an absent pack forces (`!a.packId`
 * instead of `a.packId !== pack.id`). Importing it would mean exporting internals of a
 * 719-line card-local component; the gear itself copied it from the monitoring tab for the
 * same reason (its D15). The handle normaliser is NOT duplicated — it is the shared
 * `normalizeHandle` from utils.js, exactly as the gear's add-new path imports it.
 *
 * NOT HERE. Binding an EXISTING account to an EXISTING pack (owner ruling 7 — the gear
 * keeps it), pack editing of any kind, and any second account editor: the pencil editor of
 * block 8 is replace-not-merge (`LeadDetailsInfoTab.jsx:397-402` wipes and re-inserts every
 * non-primary row, dropping `packId`), so pack-bound account creation deliberately writes
 * through `POST /api/packs`, which never touches rows it was not given.
 */
import { useEffect, useMemo, useState } from 'react';
import { useCreatePack } from '../api/packs.js';
import { useLeadAccounts } from '../api/leads.js';
import { normalizeHandle as normalizeSocialHandle } from '../utils.js';
import { PLATFORM_LABEL_SHORT } from '../constants/platforms.js';
import { APPS } from '../constants/apps.js';
import { Button, Icon, SelectNative } from './ds';

// apiClient-interceptor (api/client.js) разворачивает axios-ошибку в plain Error с
// .data/.status — .response остаётся только у сырых axios-вызовов. The second read is
// the app's own idiom (PackSettingsModal.jsx:97) and a known dead branch (recon §E.9);
// it is copied, not repaired — repairing it would change observable copy in a UX phase.
const apiErrorCode = (err) => err?.data?.error ?? err?.response?.data?.error;

/** The platform pill is MetricTable's own (ds.css:430) — the pill the gear draws too. */
function PlatformBadge ({ platform }) {
    return (
        <span className={`oc-metric-table__platform oc-metric-table__platform--${platform}`}>
            {PLATFORM_LABEL_SHORT[platform] ?? platform}
        </span>
    );
}

export function PackCreateModal ({ leadId, onClose }) {
    const accountsQ = useLeadAccounts(leadId);
    const createMut = useCreatePack();

    const [label, setLabel] = useState('');
    // Ruling 5 — «Тестовая» is the DB default (db/schema.ts:1448), what the four money
    // flows already create, and the normal start of a blogger's life (rounds exist only
    // on test packs). Switchable right here, in the same request.
    const [type, setType] = useState('test');
    // PACK-APP-REQ (owner ruling 2026-09-30): the application is mandatory at birth — no
    // default, «Создать пачку» stays off until the manager picks one.
    const [application, setApplication] = useState('');
    const [checkedIds, setCheckedIds] = useState([]);
    const [platform, setPlatform] = useState('instagram');
    const [handle, setHandle] = useState('');
    const [queued, setQueued] = useState([]);
    const [dupRefused, setDupRefused] = useState(false);

    // Escape closes — the app's existing modal convention.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const accounts = useMemo(
        () => (Array.isArray(accountsQ.data?.accounts) ? accountsQ.data.accounts : []),
        [accountsQ.data],
    );

    // The gear's filter, minus the pack it has no pack to compare against. It diverges
    // from CreateInvoiceModal's bare `!a.packId`, which also offers the donor (primary)
    // account: `POST /packs/:id/accounts` refuses primaries with `account_is_primary`,
    // so offering one here would make the two account-adding paths disagree.
    const candidates = useMemo(
        () => accounts.filter(
            (a) => !a.packId
                && !a.isPrimary
                && a.accountPurpose !== 'blogger'
                && a.status !== 'banned',
        ),
        [accounts],
    );

    const toggle = (id) => setCheckedIds(
        (prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]),
    );

    const normalized = normalizeSocialHandle(handle, platform);
    // The refusal compares against ALL of the lead's accounts on the platform, not merely
    // the step-3 candidates: without the widening a typed handle equal to the donor
    // (primary) silently binds that very row, because the server upsert conflicts on
    // (leadId, platform, handle) (packs.js:346-350) and binds whatever row comes back with
    // a null packId (:395-399) — with no isPrimary and no banned guard, unlike the sibling
    // route. Front-only in this phase; the backend asymmetry is carried debt.
    const isDuplicate = (h) => {
        const lc = String(h).toLowerCase();
        return accounts.some((a) => a.platform === platform && String(a.handle ?? '').toLowerCase() === lc)
            || queued.some((q) => q.platform === platform && q.handle.toLowerCase() === lc);
    };

    const addQueued = () => {
        if (!normalized) return;
        if (isDuplicate(normalized)) { setDupRefused(true); return; }
        setDupRefused(false);
        setQueued((prev) => [...prev, { platform, handle: normalized }]);
        setHandle('');
    };

    const dropQueued = (i) => setQueued((prev) => prev.filter((_, idx) => idx !== i));

    // «Добавить» stays off until the accounts query has SUCCEEDED: the refusal above reads
    // that query's data, so in the pending and failed states the guard would be empty and a
    // handle equal to the primary would sail through it. Creating a pack with zero accounts
    // remains possible in those states.
    const canQueue = accountsQ.isSuccess && !!normalized;

    const submit = () => {
        createMut.mutate({
            leadId,
            type,
            application,
            // Blank name ⇒ the key is OMITTED, so the server auto-labels «Пачка N»
            // (packs.js:377). An empty string would store a blank label.
            ...(label.trim() ? { label: label.trim() } : {}),
            ...(checkedIds.length ? { accountIds: checkedIds } : {}),
            ...(queued.length ? { newAccounts: queued } : {}),
        }, { onSuccess: () => onClose?.() });
    };

    const submitError = createMut.isError
        ? (apiErrorCode(createMut.error) === 'account_already_in_pack'
            ? 'Один из аккаунтов уже в другой пачке — обнови карточку.'
            : 'Не удалось создать пачку.')
        : null;

    const isEmptyPack = checkedIds.length === 0 && queued.length === 0;

    return (
        <div className='oc-lead-card__pset-scrim' onClick={onClose}>
            <div className='oc-lead-card__pset' role='dialog' onClick={(e) => e.stopPropagation()}>
                <div className='oc-lead-card__pset-head'>
                    <span className='oc-lead-card__section-title'>Новая пачка</span>
                    <Button
                        hierarchy='tertiary'
                        size='small'
                        iconOnly
                        className='oc-lead-card__right'
                        title='Закрыть'
                        aria-label='Закрыть'
                        onClick={onClose}
                    ><Icon name='x' size='sm' /></Button>
                </div>

                <div className='oc-lead-card__pset-body'>
                    <div className='oc-lead-card__pset-row'>
                        <span className='oc-lead-card__label'>Название</span>
                        <input
                            className='oc-lead-card__input'
                            type='text'
                            placeholder='Пачка N — по умолчанию'
                            value={label}
                            disabled={createMut.isPending}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className='oc-lead-card__pset-row'>
                        <span className='oc-lead-card__label'>Тип пачки</span>
                        <SelectNative
                            compact
                            value={type}
                            disabled={createMut.isPending}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <option value='test'>Тестовая</option>
                            <option value='ambassador'>Амбассадорская</option>
                        </SelectNative>
                    </div>

                    <div className='oc-lead-card__pset-row'>
                        <span className='oc-lead-card__label'>Приложение</span>
                        <SelectNative
                            compact
                            value={application}
                            disabled={createMut.isPending}
                            onChange={(e) => setApplication(e.target.value)}
                        >
                            <option value=''>— выберите приложение —</option>
                            {APPS.map((a) => (<option key={a.value} value={a.value}>{a.value}</option>))}
                        </SelectNative>
                    </div>

                    {/* Three states, never conflated: a pending query must not flash the
                        empty text (a fast click would read as «no accounts»), and a failed
                        one must not either — an empty list is not the same as no answer. */}
                    <div className='oc-lead-card__pset-sect'>
                        <div className='oc-lead-card__label'>Аккаунты блогера</div>
                        {accountsQ.isPending ? (
                            <span className='oc-lead-card__muted'>Загрузка…</span>
                        ) : accountsQ.isError ? (
                            <div className='oc-lead-card__error'>Не удалось загрузить аккаунты.</div>
                        ) : candidates.length === 0 ? (
                            <span className='oc-lead-card__muted'>Свободных аккаунтов нет — добавь новый ниже</span>
                        ) : candidates.map((a) => (
                            <label key={a.id} className='oc-lead-card__pset-account'>
                                <input
                                    type='checkbox'
                                    checked={checkedIds.includes(a.id)}
                                    disabled={createMut.isPending}
                                    onChange={() => toggle(a.id)}
                                />
                                <PlatformBadge platform={a.platform} />
                                @{a.handle}
                            </label>
                        ))}
                    </div>

                    <div className='oc-lead-card__pset-add'>
                        <div className='oc-lead-card__label'>Новый аккаунт</div>
                        {queued.map((q, i) => (
                            <div key={`${q.platform}-${q.handle}`} className='oc-lead-card__pset-account'>
                                <PlatformBadge platform={q.platform} />
                                @{q.handle}
                                <span className='oc-lead-card__right'>
                                    <Button
                                        hierarchy='tertiary'
                                        size='small'
                                        iconOnly
                                        title='Убрать'
                                        aria-label='Убрать'
                                        disabled={createMut.isPending}
                                        onClick={() => dropQueued(i)}
                                    ><Icon name='x' size='sm' /></Button>
                                </span>
                            </div>
                        ))}
                        <div className='oc-lead-card__pset-add-row'>
                            <SelectNative
                                compact
                                value={platform}
                                disabled={createMut.isPending}
                                onChange={(e) => { setPlatform(e.target.value); setDupRefused(false); }}
                            >
                                {Object.entries(PLATFORM_LABEL_SHORT).map(([value, short]) => (
                                    <option key={value} value={value}>{short}</option>
                                ))}
                            </SelectNative>
                            <input
                                className='oc-lead-card__input'
                                type='text'
                                placeholder='новый аккаунт'
                                value={handle}
                                disabled={createMut.isPending}
                                onChange={(e) => { setHandle(e.target.value); setDupRefused(false); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') addQueued(); }}
                            />
                            <Button
                                hierarchy='secondary'
                                size='small'
                                disabled={!canQueue || createMut.isPending}
                                onClick={addQueued}
                            >Добавить</Button>
                        </div>
                        {dupRefused && (
                            <div className='oc-lead-card__error'>Этот аккаунт уже есть у блогера</div>
                        )}
                    </div>

                    {isEmptyPack && (
                        <div className='oc-lead-card__hint'>
                            Пачка без аккаунтов — аккаунты добавляются в шестерёнке
                        </div>
                    )}

                    <div className='oc-lead-card__actions'>
                        <Button
                            hierarchy='tertiary'
                            size='small'
                            disabled={createMut.isPending}
                            onClick={onClose}
                        >Отмена</Button>
                        <Button
                            hierarchy='primary'
                            size='small'
                            disabled={createMut.isPending || !application}
                            onClick={submit}
                        >{createMut.isPending ? 'Создаю…' : 'Создать пачку'}</Button>
                    </div>
                    {submitError && <div className='oc-lead-card__error'>{submitError}</div>}
                </div>
            </div>
        </div>
    );
}

export default PackCreateModal;
